// "Team tasks use a standard team of helpers. It isn't installed in this
// workspace yet." with "Install the team" (POST …/job-team/install, the
// same install as the jobs rollout's team step): one line where Team tasks
// need it (./teamModel.ts says where), nothing anywhere else. After a press
// it says what happened in one line (the helpers made, or which could not
// be, with the server's reasons in small print under it); the notice goes
// once the team is complete.

import { useState } from "react";

import { useInstallJobTeam, useJobs } from "@/api/jobs";
import { Button } from "@/components/ui/Button";
import { Notice } from "@/components/ui/Notice";
import { useAgentDirectory } from "@/hooks/useAgentDirectory";

import { jobErrorText } from "./jobModel";
import { teamButtonLabel, teamInstallDetail, teamInstalledLine, teamNoticeLine, teamNoticeShown } from "./teamModel";

interface TeamSetupNoticeProps {
  customerId: string;
  /** The operator is asking for a Team task here: shown even where none has been started yet. */
  asked?: boolean;
  className?: string;
}

export function TeamSetupNotice({ customerId, asked = false, className }: TeamSetupNoticeProps) {
  // the Runs list's own query (the same key): no second request where both show
  const jobs = useJobs(customerId || null, { limit: 100 });
  const install = useInstallJobTeam();
  const dir = useAgentDirectory(customerId);
  const [note, setNote] = useState<{ text: string; error: boolean; detail?: string } | null>(null);
  const missing = jobs.data?.team_missing ?? [];
  const shown = !!jobs.data && teamNoticeShown({ offered: jobs.data.offered, missing, used: jobs.data.team_used ?? false, asked });

  function onInstall() {
    setNote(null);
    install.mutate(
      { customerId },
      {
        onSuccess: (res) => setNote({ text: teamInstalledLine(res, dir.nameOf), error: Object.keys(res.failed ?? {}).length > 0, detail: teamInstallDetail(res, dir.nameOf) }),
        onError: (e) => setNote({ text: jobErrorText(e), error: true }),
      },
    );
  }

  if (!shown && !note) return null;
  const line = teamNoticeLine(missing, dir.nameOf);
  return (
    <div className={className}>
      {shown && (
        <Notice
          tone="info"
          action={
            <Button size="sm" variant="secondary" loading={install.isPending} onClick={onInstall} title={line.title}>
              {teamButtonLabel(missing)}
            </Button>
          }
        >
          {line.text}
        </Notice>
      )}
      {note && (
        <p role={note.error ? "alert" : "status"} className={note.error ? "mt-2 rounded-card bg-danger/10 px-4 py-3 text-sm text-danger" : "mt-2 text-sm text-fg-muted"}>
          {note.text}
          {note.detail && <span className="mt-1 block whitespace-pre-line text-xs text-fg-muted [overflow-wrap:anywhere]">{note.detail}</span>}
        </p>
      )}
    </div>
  );
}
